import React, { useState, useEffect, useCallback } from "react";
import { DollarSign, TrendingUp, Users, BarChart3, Store, Heart, Receipt, Globe, Loader2, ArrowUpRight, ArrowDownRight, Brain, Calendar, RefreshCw } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { useAuth } from "../contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MetricCard = ({ label, value, sub, icon: Icon, color = "violet", trend }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-3 space-y-1" data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between">
      <span className="text-xs text-gray-500">{label}</span>
      {Icon && <Icon className={`h-3.5 w-3.5 text-${color}-500`} />}
    </div>
    <p className="text-xl font-bold text-gray-900">{value}</p>
    <div className="flex items-center gap-1">
      {trend != null && (
        <span className={`text-xs font-medium flex items-center ${trend > 0 ? "text-green-600" : trend < 0 ? "text-red-500" : "text-gray-400"}`}>
          {trend > 0 ? <ArrowUpRight className="h-3 w-3" /> : trend < 0 ? <ArrowDownRight className="h-3 w-3" /> : null}
          {trend !== 0 ? `${Math.abs(trend)}%` : "flat"}
        </span>
      )}
      {sub && <span className="text-xs text-gray-400">{sub}</span>}
    </div>
  </div>
);

const SectionHeader = ({ icon: Icon, title, color = "gray" }) => (
  <div className="flex items-center gap-2 pt-2">
    <div className={`w-6 h-6 rounded-md bg-${color}-100 flex items-center justify-center`}>
      <Icon className={`h-3.5 w-3.5 text-${color}-600`} />
    </div>
    <h4 className="text-sm font-bold text-gray-800">{title}</h4>
  </div>
);

const AdminAnalyticsTab = () => {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [dailyReport, setDailyReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadAnalytics = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [overviewRes, reportRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/analytics/overview`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/analytics/daily-report`, { headers }),
      ]);
      if (overviewRes.ok) setData(await overviewRes.json());
      if (reportRes.ok) {
        const r = await reportRes.json();
        if (r.report) setDailyReport(r.report);
      }
    } catch { /* ignore */ }
    setLoading(false);
    setRefreshing(false);
  }, [token]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
    </div>
  );

  if (!data) return <p className="text-center text-sm text-gray-500 py-8">Failed to load analytics</p>;

  const { revenue, growth, engagement, partnership, impact } = data;

  return (
    <div className="space-y-4" data-testid="analytics-tab">

      {/* Refresh button — pull-fresh counts after edits in Users / Promos tabs */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => loadAnalytics(true)}
          disabled={refreshing}
          className="text-xs"
          data-testid="analytics-refresh-btn"
        >
          <RefreshCw className={`h-3 w-3 mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Refreshing..." : "Refresh"}
        </Button>
      </div>

      {/* AI Daily Report */}
      {dailyReport && (
        <>
          <SectionHeader icon={Brain} title="AI Daily Report" color="violet" />
          <Card className="border-violet-200 bg-gradient-to-br from-violet-50/50 to-white">
            <CardContent className="p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Badge className="bg-violet-100 text-violet-700 text-xs">{dailyReport.date}</Badge>
                <span className="text-xs text-gray-400">Generated {new Date(dailyReport.generated_at).toLocaleTimeString()}</span>
              </div>
              <div className="text-sm text-gray-700 whitespace-pre-line leading-relaxed" data-testid="ai-report-content">
                {dailyReport.ai_report}
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {!dailyReport && (
        <Card className="border-violet-200 bg-violet-50/30">
          <CardContent className="p-3 text-center">
            <Brain className="h-5 w-5 text-violet-400 mx-auto mb-1" />
            <p className="text-xs text-violet-600 font-medium">AI Daily Report</p>
            <p className="text-xs text-gray-400">First report generates automatically at 6 AM daily</p>
          </CardContent>
        </Card>
      )}

      {/* P&L Summary */}
      <SectionHeader icon={DollarSign} title="Revenue & P&L" color="green" />
      <div className="grid grid-cols-2 gap-2">
        <MetricCard label="Gross MRR" value={`$${revenue.gross_mrr?.toLocaleString()}`} sub="paid subs only" icon={DollarSign} color="green" />
        <MetricCard label="Net ARR" value={`$${revenue.arr?.toLocaleString()}`} sub="annual run rate" icon={TrendingUp} color="green" />
      </div>

      {/* User categories — split so granted users show as FREE ($0 revenue) */}
      <div className="grid grid-cols-2 gap-2" data-testid="user-category-breakdown">
        <MetricCard
          label="Paid Users"
          value={(revenue.paid_monthly || 0) + (revenue.paid_annual || 0)}
          sub={`${revenue.paid_monthly || 0} mo · ${revenue.paid_annual || 0} yr`}
          icon={DollarSign}
          color="green"
        />
        <MetricCard
          label="Trial Users"
          value={revenue.trial_users || 0}
          sub="converting"
          icon={Users}
          color="blue"
        />
        <MetricCard
          label="Free Users"
          value={(growth.total_users || 0) - ((revenue.paid_monthly || 0) + (revenue.paid_annual || 0)) - (revenue.trial_users || 0)}
          sub={`incl. ${(revenue.granted_monthly || 0) + (revenue.granted_annual || 0)} granted`}
          icon={Users}
          color="gray"
        />
        <MetricCard
          label="Total Users"
          value={growth.total_users || 0}
          sub="all accounts"
          icon={Users}
          color="violet"
        />
      </div>

      <Card className="border-green-200 bg-green-50/50">
        <CardContent className="p-3 space-y-2">
          <p className="text-xs font-semibold text-green-800 uppercase tracking-wide">Profit & Loss (Monthly)</p>
          <div className="space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-gray-600">Gross Revenue (MRR)</span><span className="font-semibold text-green-700">${revenue.gross_mrr?.toLocaleString()}</span></div>
            <div className="flex justify-between text-xs text-gray-400 pl-4">
              <span>Paid: {revenue.paid_monthly} monthly + {revenue.paid_annual} annual</span>
            </div>
            {(revenue.granted_monthly > 0 || revenue.granted_annual > 0) && (
              <div className="flex justify-between text-xs text-gray-400 pl-4">
                <span>Granted (free): {revenue.granted_monthly + revenue.granted_annual} users</span>
                <span className="text-gray-300">$0 revenue</span>
              </div>
            )}
            {revenue.stripe_revenue_30d > 0 && (
              <div className="flex justify-between"><span className="text-gray-500 text-xs">Stripe verified (30d)</span><span className="text-xs text-green-600">${revenue.stripe_revenue_30d?.toLocaleString()}</span></div>
            )}
            <div className="flex justify-between"><span className="text-gray-600">Referral Payouts Owed</span><span className="font-medium text-red-500">-${revenue.referral_payouts_owed?.toLocaleString()}</span></div>
            <div className="flex justify-between text-xs text-gray-400 pl-4">
              <span>Total earned: ${revenue.referral_total_earned?.toLocaleString()}</span>
              <span>Paid out: ${revenue.referral_total_paid?.toLocaleString()}</span>
            </div>
            <div className="flex justify-between"><span className="text-gray-600">Incentive Discounts</span><span className="font-medium text-red-500">-${revenue.incentive_discount_cost?.toLocaleString()}</span></div>
            <div className="flex justify-between text-xs text-gray-400 pl-4">
              <span>{revenue.silver_tier} silver (50% off) + {revenue.gold_tier} gold ($1.99)</span>
            </div>
            <div className="border-t border-green-200 pt-1.5 flex justify-between">
              <span className="font-bold text-gray-800">Net MRR</span>
              <span className={`font-bold text-lg ${revenue.net_mrr >= 0 ? "text-green-700" : "text-red-600"}`}>${revenue.net_mrr?.toLocaleString()}</span>
            </div>
          </div>
          <p className="text-xs text-gray-400">{revenue.total_transactions} lifetime txns / {revenue.recent_transactions_30d} last 30d / {revenue.canceled} canceled</p>
        </CardContent>
      </Card>

      {/* Growth */}
      <SectionHeader icon={TrendingUp} title="Growth Metrics" color="violet" />
      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="Total Users" value={growth.total_users} icon={Users} color="violet" />
        <MetricCard label="New (7d)" value={growth.new_users_7d} icon={TrendingUp} color="blue" />
        <MetricCard label="New (30d)" value={growth.new_users_30d} icon={TrendingUp} color="blue" trend={growth.growth_rate_mom} />
      </div>
      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="Conversion" value={`${growth.conversion_rate}%`} sub="trial-to-paid" icon={ArrowUpRight} color="green" />
        <MetricCard label="Active Rate" value={`${growth.active_rate}%`} sub="of all users" icon={Users} color="violet" />
        <MetricCard label="Churn" value={`${growth.churn_rate}%`} sub="canceled" icon={ArrowDownRight} color="red" />
      </div>

      {/* Community Engagement */}
      <SectionHeader icon={Heart} title="Community Engagement" color="orange" />
      <div className="grid grid-cols-2 gap-2">
        <MetricCard label="Deals Posted" value={engagement.total_deals_posted} sub={`${engagement.deals_7d} this week`} icon={BarChart3} color="orange" />
        <MetricCard label="Verified" value={`${engagement.verification_rate}%`} sub={`${engagement.verified_deals} deals`} icon={Heart} color="green" />
        <MetricCard label="Avg Engagement" value={engagement.avg_engagement_per_deal} sub="per deal" icon={TrendingUp} color="violet" />
        <MetricCard label="Community Users" value={engagement.community_users} icon={Users} color="orange" />
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-pink-50 rounded-lg p-2">
          <p className="text-lg font-bold text-pink-600">{engagement.total_likes}</p>
          <p className="text-xs text-gray-500">Likes</p>
        </div>
        <div className="bg-blue-50 rounded-lg p-2">
          <p className="text-lg font-bold text-blue-600">{engagement.total_comments}</p>
          <p className="text-xs text-gray-500">Comments</p>
        </div>
        <div className="bg-amber-50 rounded-lg p-2">
          <p className="text-lg font-bold text-amber-600">{engagement.total_saves}</p>
          <p className="text-xs text-gray-500">Saves</p>
        </div>
      </div>

      {engagement.top_stores?.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Top Stores by Deals</p>
            <div className="space-y-1.5">
              {engagement.top_stores.slice(0, 7).map((s, i) => (
                <div key={s.store} className="flex items-center gap-2 text-sm">
                  <span className="w-5 text-xs text-gray-400 text-right">{i + 1}.</span>
                  <span className="flex-1 font-medium text-gray-800">{s.store}</span>
                  <Badge className="bg-gray-100 text-gray-600 text-xs">{s.deals} deals</Badge>
                  <span className="text-xs text-pink-500">{s.likes} likes</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Partnership Positioning */}
      <SectionHeader icon={Globe} title="Partnership & Licensing Data" color="blue" />
      <Card className="border-blue-200 bg-blue-50/30">
        <CardContent className="p-3 space-y-3">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-500">Receipts Scanned</p>
              <p className="font-bold text-gray-900">{partnership.total_receipts_scanned}</p>
              <p className="text-xs text-green-600">{partnership.receipt_parse_accuracy}% accuracy</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Unique Stores</p>
              <p className="font-bold text-gray-900">{partnership.unique_stores_scanned}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Price Data Points</p>
              <p className="font-bold text-gray-900">{partnership.price_data_points?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Items Tracked</p>
              <p className="font-bold text-gray-900">{partnership.purchase_items_tracked?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Flyer Stores</p>
              <p className="font-bold text-gray-900">{partnership.flyer_stores_covered}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Flyers Indexed</p>
              <p className="font-bold text-gray-900">{partnership.total_flyers_indexed}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Geo Reach (Zips)</p>
              <p className="font-bold text-gray-900">{partnership.geographic_reach_zips}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Referral Network</p>
              <p className="font-bold text-gray-900">{partnership.referral_network_size} users</p>
            </div>
          </div>
          {partnership.flyer_store_names?.length > 0 && (
            <div>
              <p className="text-xs text-gray-400 mb-1">Flyer Coverage</p>
              <div className="flex flex-wrap gap-1">
                {partnership.flyer_store_names.map(s => (
                  <Badge key={s} className="bg-blue-100 text-blue-700 text-xs font-normal">{s}</Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Community Impact */}
      <SectionHeader icon={Heart} title="Community Impact" color="emerald" />
      <div className="grid grid-cols-2 gap-2">
        <MetricCard label="Total Savings" value={`$${impact.total_user_savings?.toLocaleString()}`} sub="for users" icon={DollarSign} color="emerald" />
        <MetricCard label="Receipt Savings" value={`$${impact.receipt_verified_savings?.toLocaleString()}`} sub="verified" icon={Receipt} color="green" />
        <MetricCard label="Silver Members" value={impact.silver_tier_members} sub="50% discount" icon={Store} color="gray" />
        <MetricCard label="Gold Members" value={impact.gold_tier_members} sub="$1.99/mo" icon={Store} color="amber" />
      </div>

      {impact.top_contributors?.length > 0 && (
        <Card>
          <CardContent className="p-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Top Contributors</p>
            <div className="space-y-1.5">
              {impact.top_contributors.map((c, i) => (
                <div key={c.username || i} className="flex items-center gap-2 text-sm">
                  <span className="w-5 text-xs text-gray-400 text-right">#{i + 1}</span>
                  <span className="flex-1 font-medium text-gray-800">{c.username || "Anonymous"}</span>
                  <Badge className="bg-violet-100 text-violet-700 text-xs">Lv.{c.level || 1}</Badge>
                  <span className="text-xs text-gray-500">{c.points || 0} pts</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-gray-300 text-center pt-2">
        Real-time data as of {new Date(data.generated_at).toLocaleString()}
      </p>
    </div>
  );
};

export default AdminAnalyticsTab;
